// Helper functions
function getCurrentPageFromQuery() {
  const params = new URLSearchParams(window.location.search);
  const page = parseInt(params.get('page'), 10);
  return isNaN(page) ? 1 : page;
}

function getCurrentViewport() {
  const width = window.innerWidth;
  if (width >= 1024) return 'desktop';
  if (width >= 641) return 'tablet';
  return 'mobile';
}

function getColumnsPerRow(productGrid, viewport) {
  // Try to get from data attribute first
  const attrValue = parseInt(productGrid.getAttribute(`data-products-per-row-${viewport}`), 10);
  if (attrValue) return attrValue;

  // Fallback: compute from CSS grid-template-columns
  const computedStyle = window.getComputedStyle(productGrid);
  const columns = computedStyle.gridTemplateColumns;
  if (columns) {
    const columnCount = columns.split(' ').filter((c) => c.trim().length > 0).length;
    if (columnCount > 0) return columnCount;
  }

  // Final fallback
  return { desktop: 4, tablet: 3, mobile: 2 }[viewport];
}

function calculateEffectiveProductSpace(baseTotalProductsDisplayed) {
  let totalBannerPositions = 0;
  const viewport = getCurrentViewport();

  // Sum up space occupied by currently visible banners
  document.querySelectorAll('.banner__block:not(.hidden)').forEach((banner) => {
    const rowSpan = parseInt(banner.getAttribute(`row-span-${viewport}`), 10) || 1;
    const colSpan = parseInt(banner.getAttribute(`col-span-${viewport}`), 10) || 1;
    const bannerPositions = rowSpan * colSpan;
    totalBannerPositions += bannerPositions;
  });

  // Total grid positions available = products + space taken by banners
  const totalGridPositions = baseTotalProductsDisplayed + totalBannerPositions;
  return totalGridPositions;
}

function calculateGridRows(baseTotalProductsDisplayed, columnsPerRow) {
  const effectiveSpace = calculateEffectiveProductSpace(baseTotalProductsDisplayed);
  return Math.ceil(effectiveSpace / columnsPerRow);
}

function getVisibleAbsoluteRows(baseTotalProductsDisplayed, columnsPerRow, productsPerPage, minPage) {
  const totalRowsVisible = calculateGridRows(baseTotalProductsDisplayed, columnsPerRow);
  const firstAbsoluteRow = Math.ceil(((minPage - 1) * productsPerPage + 1) / columnsPerRow);

  return Array.from({ length: totalRowsVisible }, (_, i) => firstAbsoluteRow + i);
}

function isBannerPositionValid(relativeRowIndex, positionColumn, baseTotalProductsDisplayed, columnsPerRow) {
  // Recalculate effective space considering current visible banners
  const effectiveSpace = calculateEffectiveProductSpace(baseTotalProductsDisplayed);

  const elementsUpToPosition = relativeRowIndex * columnsPerRow + positionColumn;
  return elementsUpToPosition <= effectiveSpace;
}

function applyBannerStyles(banner, relativeRow, positionColumn) {
  const viewport = getCurrentViewport();
  const rowSpan = parseInt(banner.getAttribute(`row-span-${viewport}`), 10) || 1;
  const colSpan = parseInt(banner.getAttribute(`col-span-${viewport}`), 10) || 1;

  banner.style.gridRow = rowSpan > 1 ? `${relativeRow} / span ${rowSpan}` : relativeRow;
  banner.style.gridColumn = colSpan > 1 ? `${positionColumn} / span ${colSpan}` : positionColumn;

  return { rowSpan, colSpan };
}

/**
 * Updates the last-in-row class on product grid items based on
 * actual visual screen position (getBoundingClientRect) rather than DOM order.
 * This correctly handles banner blocks that are positioned via grid-row/grid-column.
 * Only applies on desktop viewport (>= 1024px).
 */
function updateLastInRow() {
  var productGrid = document.querySelector('.product-grid');
  if (!productGrid) return;

  // Get all visible items: product cards + non-hidden banners
  var allItems = productGrid.querySelectorAll('.product-grid__item, .banner__block:not(.hidden)');

  // Reference: right edge of the grid container (with 2px tolerance for borders/subpixel)
  var gridRect = productGrid.getBoundingClientRect();
  var gridRight = gridRect.right - 2;

  // Group elements by their vertical screen position (row)
  // Use Math.round to handle subpixel rendering differences
  var rows = new Map();

  allItems.forEach(function (item) {
    var rect = item.getBoundingClientRect();
    var rowKey = Math.round(rect.top);

    if (!rows.has(rowKey)) {
      rows.set(rowKey, []);
    }
    rows.get(rowKey).push({ element: item, right: rect.right });
  });

  // Remove last-in-row from all product grid items first
  productGrid.querySelectorAll('.product-grid__item.last-in-row').forEach(function (item) {
    item.classList.remove('last-in-row');
  });

  // For each row, find the rightmost product-grid__item (ignore banners).
  // Only apply last-in-row if the row is full (rightmost item reaches grid edge).
  rows.forEach(function (items) {
    var maxRight = 0;
    var lastProductItem = null;

    items.forEach(function (entry) {
      if (entry.element.classList.contains('product-grid__item') && entry.right > maxRight) {
        maxRight = entry.right;
        lastProductItem = entry.element;
      }
    });

    if (lastProductItem && maxRight >= gridRight) {
      lastProductItem.classList.add('last-in-row');
    }
  });
}

function insertBannerBlocks() {
  // Update page tracking
  const currentPage = getCurrentPageFromQuery();
  if (window.minPage === null || window.maxPage === null) {
    window.minPage = window.maxPage = currentPage;
  } else {
    window.minPage = Math.min(window.minPage, currentPage);
    window.maxPage = Math.max(window.maxPage, currentPage);
  }

  // Get DOM elements and configuration
  const productGrid = document.querySelector('.product-grid');
  if (!productGrid) return;

  // Keep the banners inside their `.collection-banners` wrapper (so the markup
  // stays stable and the section morph used when re-rendering results can
  // reconcile the banners), but make the wrapper transparent to the grid so the
  // banner blocks still participate in the product grid layout directly.
  //
  // When load-more / infinite-scroll appends subsequent pages, each page's
  // section HTML also contains a `.collection-banners` wrapper with the same
  // banner blocks. Those duplicate wrappers would appear as unpositioned grid
  // items (the banners inside them are nested, not direct grid children, so
  // their grid-row/grid-column styles have no effect). Keep only the first
  // wrapper visible and hide any duplicates.
  const collectionBannersWrappers = productGrid.querySelectorAll('.collection-banners');
  collectionBannersWrappers.forEach((wrapper, index) => {
    if (index === 0) {
      wrapper.style.display = 'contents';
    } else {
      wrapper.style.display = 'none';
    }
  });

  const viewport = getCurrentViewport();
  const columnsPerRow = getColumnsPerRow(productGrid, viewport);

  const productsPerPage = parseInt(productGrid.getAttribute('data-products-per-page'), 10) || 12;

  const baseTotalProductsDisplayed = productGrid.querySelectorAll('.product-grid__item').length;

  // Reset all banners to hidden and set viewport-specific position attributes
  productGrid.querySelectorAll('.banner__block').forEach((banner) => {
    banner.classList.add('hidden');

    // Set the current viewport position attributes for easier access
    const positionRow = banner.getAttribute(`position-row-${viewport}`);
    const positionColumn = banner.getAttribute(`position-column-${viewport}`);

    if (positionRow && positionColumn) {
      banner.setAttribute('position-row', positionRow);
      banner.setAttribute('position-column', positionColumn);
    } else {
      // If no position for current viewport, ensure banner stays hidden
      banner.removeAttribute('position-row');
      banner.removeAttribute('position-column');
    }
  });

  // Main processing
  const visibleAbsoluteRows = getVisibleAbsoluteRows(
    baseTotalProductsDisplayed,
    columnsPerRow,
    productsPerPage,
    window.minPage,
  );
  insertVisibleBanners(visibleAbsoluteRows);

  function insertVisibleBanners(visibleRows) {
    let bannersInserted = false;

    visibleRows.forEach((absoluteRow, index) => {
      const bannerBlocks = productGrid.querySelectorAll(`.banner__block[position-row="${absoluteRow}"]`);
      bannerBlocks.forEach((banner) => {
        const positionColumn = parseInt(banner.getAttribute('position-column'), 10);
        const relativeRow = index + 1;

        if (isBannerPositionValid(index, positionColumn, baseTotalProductsDisplayed, columnsPerRow)) {
          if (banner.classList.contains('hidden')) {
            banner.classList.remove('hidden');
            bannersInserted = true;
          }

          applyBannerStyles(banner, relativeRow, positionColumn);
        }
      });
    });

    // Se la griglia è cambiata, ricalcola righe e reinserisci
    if (bannersInserted) {
      requestAnimationFrame(() => {
        const newVisibleRows = getVisibleAbsoluteRows(
          baseTotalProductsDisplayed,
          columnsPerRow,
          productsPerPage,
          window.minPage,
        );
        insertVisibleBanners(newVisibleRows);
        // Recalculate after recursive repositioning
        updateLastInRow();
      });
    }
  }

  // Recalculate last-in-row after banners are positioned.
  updateLastInRow();
}

/**
 * Resets pagination tracking and re-positions the banner blocks.
 * Called after a facet/sort update re-renders the results: the list always
 * restarts from the first page, so the pages accumulated through infinite
 * scroll / load more must be discarded, otherwise the banners would be
 * positioned relative to the wrong page.
 */
function refreshBannerBlocks() {
  window.minPage = null;
  window.maxPage = null;
  insertBannerBlocks();
}

// Initialize on DOM ready
// With defer attribute, readyState is 'interactive' when script runs (DOM already parsed)
// Without defer, readyState is 'loading' and we need to wait for DOMContentLoaded
function initBannerBlocks() {
  // Initialize minPage/maxPage if not set
  if (
    typeof window.minPage === 'undefined' ||
    typeof window.maxPage === 'undefined' ||
    Number.isNaN(window.minPage) ||
    Number.isNaN(window.maxPage)
  ) {
    window.minPage = null;
    window.maxPage = null;
  }

  const productGrid = document.querySelector('.product-grid');

  if (productGrid) {
    // Disconnect existing observer if any
    if (window.collectionProductListObserver) {
      window.collectionProductListObserver.disconnect();
    }

    const observer = new MutationObserver((mutationsList) => {
      let productCardMutation = false;

      for (const mutation of mutationsList) {
        if (mutation.type !== 'childList') {
          continue;
        }

        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) {
            return;
          }

          const isProductCardNode =
            (node.matches && (node.matches('product-card') || node.matches('.product-card'))) ||
            (node.querySelector && node.querySelector('product-card, .product-card'));

          if (!isProductCardNode) {
            return;
          }

          const itemsCountSpan = document.getElementById('item-count');

          if (itemsCountSpan) {
            const customElementCardsCount = productGrid.querySelectorAll('product-card').length;
            const cardsCount =
              customElementCardsCount > 0
                ? customElementCardsCount
                : productGrid.querySelectorAll('.product-card').length;

            itemsCountSpan.textContent = cardsCount;
          }

          productCardMutation = true;
        });
      }

      if (productCardMutation) {
        insertBannerBlocks();
      }
    });

    observer.observe(productGrid, {
      childList: true,
      subtree: false,
    });

    window.collectionProductListObserver = observer;
  }

  insertBannerBlocks();

  // Re-position the banner blocks after a facet/sort update re-renders the
  // results, so they keep their fixed grid position regardless of the selected
  // sorting/filtering. The event is dispatched by facets.js right after the
  // section has been morphed in (inside the view transition update).
  document.addEventListener('facet:update', function () {
    refreshBannerBlocks();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initBannerBlocks();
  });
} else {
  // DOM already parsed (defer script or script at end of body)
  initBannerBlocks();
}

// Re-run on window resize (debounced)
let resizeTimeout;
window.addEventListener('resize', () => {
  clearTimeout(resizeTimeout);
  resizeTimeout = setTimeout(insertBannerBlocks, 150);
});

// Expose for manual re-init if needed
window.insertBannerBlocks = insertBannerBlocks;
window.refreshBannerBlocks = refreshBannerBlocks;
window.updateLastInRow = updateLastInRow;
